import { Grid, Typography } from '@material-ui/core';
import { CpfCnpjMask } from 'components/masks';
import { MdSpace, SmSpace, Section } from 'm2m.components';
import React, { useState, useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import { RootState } from 'store';
import { TipoFundo, InvestidorFundoResponse } from 'common/genericTypes';
import { useParams } from 'react-router-dom';
import { makeStyles, createStyles } from '@material-ui/core/styles';

const useStyles = makeStyles(() =>
  createStyles({
    label: {
      fontWeight: 'bold',
      marginBottom: '4px',
    },
    value: {
      marginBottom: '16px',
    },
    section: {
      marginBottom: '24px',
    },
  })
);

interface FundoInvestidorDetailsProps {
  investidorId?: string;
}

const mapStateToProps = (state: RootState) => ({
  investidorApi: state.investidorApi,
});

type Props = FundoInvestidorDetailsProps & ReturnType<typeof mapStateToProps>;

const FundoInvestidorDetails: React.FC<Props> = (props) => {
  const { investidorApi } = props;
  const classes = useStyles();
  const dispatch = useDispatch();
  const { id } = useParams<{ id: string }>();
  const investidorId = props.investidorId || id;

  const [fundoData, setFundoData] = useState<InvestidorFundoResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchFundoInvestidor = async () => {
      if (!investidorId) {
        setError('ID do investidor não fornecido');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await investidorApi.getFundoInvestidorByInvestidorId(dispatch, investidorId, true);
        if (response) {
          setFundoData(response);
        } else {
          setError('Não foi possível obter os dados do fundo investidor');
        }
      } catch (error) {
        console.error('Error fetching fund investor data:', error);
        setError('Erro ao buscar dados do fundo investidor');
      } finally {
        setLoading(false);
      }
    };

    fetchFundoInvestidor();
  }, [investidorId, dispatch, investidorApi]);

  if (loading) {
    return <Typography>Carregando dados do fundo investidor...</Typography>;
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  if (!fundoData || !fundoData.fundoInvestidor) {
    return <Typography>Nenhum dado encontrado para este fundo investidor</Typography>;
  }

  const fundo = fundoData.fundoInvestidor;

  const DisplayField = ({ label, value }: { label: string; value: string | number | boolean | null | undefined }) => (
    <Grid item xs={12} sm={6} md={4}>
      <Typography variant="subtitle2" className={classes.label}>
        {label}
      </Typography>
      <Typography variant="body1" className={classes.value}>
        {value !== undefined && value !== null ? value.toString() : '-'}
      </Typography>
    </Grid>
  );

  return (
    <div>
      <Section title="Informações do Fundo" defaultGrid>
        <DisplayField label="Nome Fantasia" value={fundo.nomeFantasia} />
        <DisplayField label="CNPJ do Fundo" value={CpfCnpjMask(fundo.cnpjFundo)} />
        <DisplayField label="Código do Ativo" value={fundo.codigoAtivo} />
        <DisplayField label="Classe" value={fundo.classe} />
        <DisplayField label="Classificação ANBIMA" value={fundo.classificacaoANBIMA} />
        <DisplayField label="Código ANBIMA" value={fundo.codigoANBIMA} />
        <DisplayField label="Tipo do Fundo" value={fundo.tipoFundo} />
        <DisplayField label="Fundo Exclusivo" value={fundo.fundoExclusivo ? 'Sim' : 'Não'} />
      </Section>

      <Section title="Administrador" defaultGrid>
        <DisplayField label="Razão Social do Administrador" value={fundo.razaoSocialAdministrador} />
        <DisplayField label="CNPJ do Administrador" value={CpfCnpjMask(fundo.cnpjAdministrador)} />
      </Section>

      <Section title="Gestor" defaultGrid>
        <DisplayField label="Razão Social do Gestor" value={fundo.razaoSocialGestor} />
        <DisplayField label="CNPJ do Gestor" value={CpfCnpjMask(fundo.cnpjGestor)} />
      </Section>

      <Section title="Escriturador" defaultGrid>
        <DisplayField label="Nome do Escriturador" value={fundo.nomeSimplificadoEscriturador} />
        <DisplayField label="CNPJ do Escriturador" value={CpfCnpjMask(fundo.cnpjEscriturador)} />
      </Section>

      <Section title="Custodiante" defaultGrid>
        <DisplayField label="Razão Social do Custodiante" value={fundo.razaoSocialCustodiante} />
        <DisplayField label="CNPJ do Custodiante" value={CpfCnpjMask(fundo.cnpjCustodiante)} />
      </Section>

      <Section title="Banco Liquidante" defaultGrid>
        <DisplayField label="Razão Social do Banco Liquidante" value={fundo.razaoSocialBancoLiquidante} />
        <DisplayField label="CNPJ do Banco Liquidante" value={CpfCnpjMask(fundo.cnpjBancoLiquidante)} />
        <DisplayField label="Agência" value={fundo.agencia} />
        <DisplayField label="Conta Movimento" value={fundo.contaMovimento} />
      </Section>
    </div>
  );
};

export default connect(mapStateToProps)(FundoInvestidorDetails);
